import { useSyncExternalStore } from 'react';

export type BookingStage = 0 | 1 | 2 | 3;

export interface BookingItinerarySlot {
  time: string;
  detail: string;
}

export interface BookingItineraryDay {
  day: number;
  summary?: string;
  slots: BookingItinerarySlot[];
}

export interface BookingDashboardSnapshot {
  tripKey: string;
  destinationName: string;
  destinationCountry: string;
  city: string;
  planId: string;
  planName: string;
  planType: string;
  origin: string;
  season: string;
  visa: string;
  style: string;
  party: string;
  days: number | null;
  budget: number | null;
  estimatedCost: number | null;
  dining: string;
  pace: string;
  budgetMix: string;
  daysMix: string;
  itinerary: BookingItineraryDay[];
  remainingDays: number;
  flightLabel: string;
  hotelLabel: string;
  stage: BookingStage;
}

type Listener = () => void;

let snapshot: BookingDashboardSnapshot | null = null;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

function sameTrip(left: BookingDashboardSnapshot | null, right: BookingDashboardSnapshot): boolean {
  return left?.tripKey === right.tripKey;
}

export function seedBookingDashboard(next: BookingDashboardSnapshot): void {
  if (sameTrip(snapshot, next)) {
    const previous = snapshot;
    snapshot = {
      ...next,
      stage: previous?.stage ?? 0,
      flightLabel: previous?.flightLabel || next.flightLabel,
      hotelLabel: previous?.hotelLabel || next.hotelLabel,
    };
  } else {
    snapshot = next;
  }
  emit();
}

export function advanceBookingDashboard(stage: BookingStage, labels?: { flight?: string; hotel?: string }): void {
  if (!snapshot) {
    return;
  }
  snapshot = {
    ...snapshot,
    stage: Math.max(snapshot.stage, stage) as BookingStage,
    flightLabel: labels?.flight || snapshot.flightLabel,
    hotelLabel: labels?.hotel || snapshot.hotelLabel,
  };
  emit();
}

export function subscribeBookingDashboard(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getBookingDashboardSnapshot(): BookingDashboardSnapshot | null {
  return snapshot;
}

export function useBookingDashboard(): BookingDashboardSnapshot | null {
  return useSyncExternalStore(
    subscribeBookingDashboard,
    getBookingDashboardSnapshot,
    getBookingDashboardSnapshot,
  );
}
